import React, { useRef, useEffect, useState } from "react";
import { Box, Button, MenuItem, Select, IconButton } from "@mui/material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import LinkIcon from "@mui/icons-material/Link";
import YouTubeIcon from "@mui/icons-material/YouTube";
import DOMPurify from "dompurify";

const CustomTextEditor = ({ value, onChange }) => {
  const editorRef = useRef(null);
  const [fontSize, setFontSize] = useState("16px");

  const applyCommand = (command, value = null) => {
    const sanitizedValue = DOMPurify.sanitize(value); // sanitize input
    document.execCommand(command, false, sanitizedValue);
    editorRef.current.focus();
  };

  const insertLink = () => {
    const url = prompt("Enter URL:");
    if (url) {
      const sanitizedUrl = DOMPurify.sanitize(url); // sanitize URL
      applyCommand("createLink", sanitizedUrl);
    }
  };

  const embedYouTube = () => {
    const url = prompt("Enter YouTube video URL:");
    if (url) {
      const sanitizedUrl = DOMPurify.sanitize(url); // sanitize URL
      const embed = `<iframe width="100%" height="300" src="${sanitizedUrl.replace(
        "watch?v=",
        "embed/"
      )}" frameborder="0" allowfullscreen></iframe>`;
      document.execCommand("insertHTML", false, embed);
    }
  };
  const handleFontSizeChange = (e) => {
    setFontSize(e.target.value);
    applyCommand("fontSize", "7");
    const fonts = document.getElementsByTagName("font");
    for (let i = 0; i < fonts.length; i++) {
      if (fonts[i].size === "7") {
        fonts[i].removeAttribute("size");
        fonts[i].style.fontSize = e.target.value;
      }
    }
  };

  // Update contentEditable when value changes externally
  useEffect(() => {
    if (editorRef.current && value !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = value || "";
    }
  }, [value]);

  return (
    <Box sx={{ width: "100%" }}>
      {/* Toolbar */}
      <Box sx={{ display: "flex", gap: 0.5, mb: 1, alignItems: "center", flexWrap: "wrap", p: 0.5, bgcolor: "#f8fafc", borderRadius: 2, border: "1px solid #e2e8f0" }}>
        <Select size="small" value={fontSize} onChange={handleFontSizeChange} sx={{ minWidth: 70, fontSize: 11, "& .MuiSelect-select": { py: 0.5 } }}>
          <MenuItem value="12px">S</MenuItem>
          <MenuItem value="16px">M</MenuItem>
          <MenuItem value="20px">L</MenuItem>
          <MenuItem value="24px">XL</MenuItem>
        </Select>

        <IconButton size="small" onClick={() => applyCommand("bold")}><FormatBoldIcon fontSize="small" /></IconButton>
        <IconButton size="small" onClick={() => applyCommand("italic")}><FormatItalicIcon fontSize="small" /></IconButton>
        <IconButton size="small" onClick={() => applyCommand("underline")}><FormatUnderlinedIcon fontSize="small" /></IconButton>
        <IconButton size="small" onClick={() => applyCommand("insertUnorderedList")}><FormatListBulletedIcon fontSize="small" /></IconButton>
        <IconButton size="small" onClick={() => applyCommand("insertOrderedList")}><FormatListNumberedIcon fontSize="small" /></IconButton>
        <IconButton size="small" onClick={insertLink}><LinkIcon fontSize="small" /></IconButton>
        <IconButton size="small" onClick={embedYouTube}><YouTubeIcon fontSize="small" /></IconButton>
      </Box>

      {/* Editable Area */}
      <Box
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={(e) => onChange(e.currentTarget.innerHTML)}
        sx={{
          minHeight: "80px",
          maxHeight: "120px",
          overflowY: "auto",
          padding: "10px 12px",
          border: "1px solid #e2e8f0",
          borderRadius: "10px",
          backgroundColor: "#fff",
          fontSize: fontSize || "14px",
          lineHeight: 1.5,
          "&:focus": { outline: "none", borderColor: "#6366f1", boxShadow: "0 0 0 3px rgba(99,102,241,0.1)" },
        }}
      >
        {/* innerHTML will be handled by useEffect */}
      </Box>

      {/* <OptionList /> */}
    </Box>
  );
};

export default CustomTextEditor;
